import React from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import ProtectedRoute from "./ProtectedRoute";
import useClerkToken from "./hooks/useClerkToken";
import { useExtensionAuthFlow } from "./hooks/useExtensionAuthFlow";

import SearchInsights from "./pages/SearchInsights";
import MarketSearchPage from "./pages/MarketSearch/MarketSearchPage";
import DashboardPage from "./pages/Dashboard";
import AccountSettings from "./pages/AccountSettings";
import Support from "./pages/Support";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import SubscriptionConfirmation from "./pages/SubscriptionConfirmation";
import InactiveSubscription from "./pages/InactiveSubscription";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SignIn from "./pages/SignIn";
import FoundingAccessPage from "./pages/FoundingAccess/FoundingAccessPage";
import Main from "./components/layout/Main";

import "antd/dist/antd.css";
import "./assets/styles/main.css";
import "./assets/styles/responsive.css";
import "./styles/mobile-side-menu.css";
import './global.css';
import './clerk-override.css';
import PrivateListingRecommendations from "./pages/RecommendedListings";
import PrivateListings from "./pages/PrivateListings";
import DealerInsights from "./pages/DealerInsights";
import BuySignals from "./pages/BuySignals";
import InventoryCommandCenterPage from "./pages/InventoryCommandCenter/InventoryCommandCenterPage";


function App() {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  useClerkToken(); // Token logic after Clerk is ready
  useExtensionAuthFlow(); // Chrome extension auth flow

  // Add route tracking for styling
  React.useEffect(() => {
    document.body.setAttribute('data-route', location.pathname);
    return () => {
      document.body.removeAttribute('data-route');
    };
  }, [location.pathname]);

  return (
    <div className="App">
      <Switch>
        {/* Public Routes */}
        <Route path="/sign-in" exact component={SignIn} />
        <Route path="/sign-up" exact>
          <Redirect to="/onboarding?step=signup" />
        </Route>
        <Route path="/pricing" exact component={Pricing} />
        <Route path="/privacy-policy" exact component={PrivacyPolicy} />
        <Route path="/terms-of-service" exact component={TermsOfService} />
        <Route path="/subscription-confirmation" exact component={SubscriptionConfirmation} />
        <Route path="/inactive-subscription" exact component={InactiveSubscription} />
        <Route path="/onboarding" exact component={Onboarding} />
        <Route path="/founding" exact component={FoundingAccessPage} />
        <Route path="/recommended" exact>
          <Redirect to="/founding" />
        </Route>

        {/* Protected Routes */}
        <ProtectedRoute path={["/dashboard", "/search", "/recommendations", "/buy-signals", "/insights", "/profile", "/support", "/private-listings", "/dealer-insights", "/inventory-command-center", "/billing"]}>
          <Main>
            <Route exact path="/dashboard" component={DashboardPage} key="dashboard" />
            <Route exact path="/search" component={MarketSearchPage} key="search" />
            <Route exact path="/recommendations" component={PrivateListingRecommendations} key="recommendations" />
            <Route exact path="/buy-signals" component={BuySignals} key="buy-signals" />
            <Route exact path="/private-listings" component={PrivateListings} key="private-listings" />
            <Route exact path="/dealer-insights" component={DealerInsights} key="dealer-insights" />
            <Route exact path="/inventory-command-center" component={InventoryCommandCenterPage} key="inventory-command-center" />
            <Route path="/insights/:year/:make/:model/:trim" component={SearchInsights} key="insights" />
            <Route exact path="/profile" component={AccountSettings} key="profile" />
            <Route exact path="/billing" component={AccountSettings} key="billing" />
            <Route exact path="/support" component={Support} key="support" />
          </Main>
        </ProtectedRoute>

        {/* Root redirect - if signed in, go to dashboard */}
        <Route exact path="/">
          {!isLoaded ? null : isSignedIn ? <Redirect to="/dashboard" /> : <Redirect to="/sign-in" />}
        </Route>

        {/* Conditional catch-all redirect */}
        {isLoaded && !isSignedIn && <Redirect to="/onboarding?step=signup" />}
      </Switch>
    </div>
  );
}

export default App;